import { useState } from "react";
import { useTranslation } from "react-i18next";
import { STAR_PATH } from "@/components/StarRating/StarRating";
import "@/components/StarRatingInput/StarRatingInput.scss";

interface StarRatingInputProps {
  /** 0 when nothing is selected yet. */
  value: number;
  onChange: (rating: number) => void;
  legend: string;
}

/**
 * Pick a score from 1 to 5. Built on native radio buttons, so arrow keys, focus and screen readers
 * work as for any radio group; the stars are only their styled labels.
 */
function StarRatingInput({ value, onChange, legend }: StarRatingInputProps) {
  const { t } = useTranslation();
  const [hovered, setHovered] = useState(0);
  const shown = hovered || value;

  return (
    <fieldset className="star-rating-input" onMouseLeave={() => setHovered(0)}>
      <legend>{legend}</legend>
      <div className="star-rating-input__stars">
        {[1, 2, 3, 4, 5].map((rating) => (
          <label
            key={rating}
            className={`star-rating-input__star${
              rating <= shown ? " star-rating-input__star--on" : ""
            }`}
            onMouseEnter={() => setHovered(rating)}
          >
            <input
              type="radio"
              name="rating"
              value={rating}
              checked={value === rating}
              onChange={() => onChange(rating)}
            />
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d={STAR_PATH} />
            </svg>
            <span className="star-rating-input__sr">
              {t("reviews.stars", { count: rating })}
            </span>
          </label>
        ))}
        <span className="star-rating-input__label" aria-hidden="true">
          {shown > 0 ? t(`reviews.scoreLabel.${shown}`) : ""}
        </span>
      </div>
    </fieldset>
  );
}

export default StarRatingInput;
export type { StarRatingInputProps };
